import { Stack, TextField } from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { FormDialog } from '../../shared/components/FormDialog';
import { useToast } from '../../shared/stores/toast.store';
import { describeApiError } from '../../shared/api/errors';
import { employeePasswordSchema, type EmployeePasswordFormValues } from './schemas';
import { useEmployeeLoginHooks } from './hooks';
import type { EmployeeDto } from './types';

export interface EmployeePasswordDialogProps {
  open: boolean;
  employee: EmployeeDto | null;
  onClose: () => void;
}

const DEFAULT_VALUES: EmployeePasswordFormValues = { senha: '' };

/**
 * Sub-modal de "Definir senha" do colaborador. Aberto sobre o EmployeeFormDialog.
 *
 * Não usa `useCrudFormDialog` (que é para CRUD padrão Create/Update); a ação de
 * password é stateless do ponto de vista do form (sempre uma operação set).
 */
export function EmployeePasswordDialog({
  open,
  employee,
  onClose,
}: EmployeePasswordDialogProps): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const { usePassword } = useEmployeeLoginHooks();
  const passwordMutation = usePassword();

  const form = useForm<EmployeePasswordFormValues>({
    resolver: zodResolver(employeePasswordSchema),
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    if (open) form.reset(DEFAULT_VALUES);
  }, [open, form]);

  return (
    <FormDialog
      open={open && employee !== null}
      title={t('features.colaboradores.passwordDialog.title', { nome: employee?.nome ?? '' })}
      form={form}
      onSubmit={async (values) => {
        if (!employee) return;
        try {
          await passwordMutation.mutateAsync({ id: employee.id, input: { senha: values.senha } });
          toast.success(t('features.colaboradores.toasts.passwordSet'));
          onClose();
        } catch (err) {
          toast.error(describeApiError(err));
        }
      }}
      onCancel={onClose}
      submitLabel={t('features.colaboradores.passwordDialog.submit')}
      maxWidth="xs"
    >
      <Stack spacing={2}>
        <TextField
          type="password"
          label={t('features.colaboradores.passwordDialog.field')}
          fullWidth
          autoComplete="new-password"
          inputProps={{ maxLength: 60 }}
          error={Boolean(form.formState.errors.senha)}
          helperText={
            form.formState.errors.senha?.message ??
            t('features.colaboradores.passwordDialog.helper')
          }
          {...form.register('senha')}
        />
      </Stack>
    </FormDialog>
  );
}
